import { useCallback, useMemo } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { XIcon } from 'lucide-react';
import { Resolver, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { toast } from 'sonner';
import * as yup from 'yup';

import { credentialsEndpoints } from '@/api/endpoints';
import {
  SidebarWrapper,
  SidebarWrapperContent,
  SidebarWrapperFooter,
  SidebarWrapperForm,
  SidebarWrapperTitle,
} from '@/components/atoms/SidebarWrapper/SidebarWrapper';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger } from '@/components/ui/select';
import { setSidebarContent } from '@/redux/sidebar/actions';
import { trackEvent } from '@/segment/segment';
import { EventName } from '@/shared/types/segment';
import { createNameFieldSchema } from '@/util/validation/schema-builders';

import { apiKeyTypeLabelMapper } from './helper';
import { APIKeyCreatedData } from '../../components/ModalAPIKeyCreated/ModalAPIKeyCreated';
import useCredentials from '../../hooks/useCredentials';
import { CredentialType } from '../../types';

interface FormValues {
  name: string;
  type: CredentialType | '';
}

interface GenerateAPIKeyPanelProps {
  onKeyCreated: (data: APIKeyCreatedData) => void;
}

export const GenerateAPIKeyPanel = ({ onKeyCreated }: GenerateAPIKeyPanelProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { createCredential, createStatus } = useCredentials();

  const CREDENTIAL_TYPES = useMemo(
    () => [
      {
        value: 'analytics',
        label: t('developer_tools.api_form.type.analytics'),
        description: t('developer_tools.api_form.type.analytics_description'),
      },
      {
        value: 'manage',
        label: t('developer_tools.api_form.type.manage'),
        description: t('developer_tools.api_form.type.manage_description'),
      },
      {
        value: 'use',
        label: t('developer_tools.api_form.type.use'),
        description: t('developer_tools.api_form.type.use_description'),
      },
    ],
    [t]
  );

  const credentialSchema = useMemo(
    () =>
      yup
        .object()
        .shape({
          name: createNameFieldSchema({ getEndpoint: credentialsEndpoints.checkName }),
          type: yup
            .string()
            .oneOf(['analytics', 'manage', 'use'], t('validation.required'))
            .required(t('validation.required')),
        }),
    [t]
  );

  const form = useForm<FormValues>({
    mode: 'onChange',
    resolver: yupResolver(credentialSchema) as Resolver<FormValues>,
    defaultValues: { name: '', type: '' },
  });

  const handleClose = useCallback(() => {
    dispatch(setSidebarContent(null));
  }, [dispatch]);

  const onSubmit = useCallback(
    (data: FormValues) => {
      if (!data.type) {
        return;
      }
      trackEvent(EventName.ClickGenerateAPIKey);

      createCredential(data as { name: string; type: CredentialType }, {
        onSuccess: (resp) => {
          onKeyCreated({
            apiKey: resp.key,
            name: resp.name,
            type: apiKeyTypeLabelMapper(resp.type, t),
          });
          toast.success(t('developer_tools.credentials.created', { 0: resp.name }));
          form.reset();
          form.setFocus('name');
        },
        onError: (error) => {
          toast.error(error.message);
        },
      });
    },
    [createCredential, form, onKeyCreated, t]
  );

  const isPending = createStatus === 'pending';
  const isSubmitDisabled = isPending;

  return (
    <SidebarWrapper>
      <SidebarWrapperTitle className="pr-4 pl-6">
        <span className="text-base font-medium text-foreground">
          {t('developer_tools.api_form.title')}
        </span>
        <Button variant="ghost-neutral" size="icon-sm" onClick={handleClose}>
          <XIcon size={16} />
        </Button>
      </SidebarWrapperTitle>

      <Form {...form}>
        <SidebarWrapperForm onSubmit={form.handleSubmit(onSubmit)}>
          <SidebarWrapperContent className="px-1 pb-1">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('common.name')}</FormLabel>
                  <FormControl>
                    <Input {...field} autoFocus />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="type"
              render={({ field }) => {
                const selectedType = CREDENTIAL_TYPES.find((ct) => ct.value === field.value);

                return (
                  <FormItem>
                    <FormLabel>{t('common.type')}</FormLabel>
                    <Select
                      value={field.value}
                      onValueChange={(value) => {
                        field.onChange(value as CredentialType);
                      }}
                    >
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <span>{selectedType?.label ?? t('common.select_dots')}</span>
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {CREDENTIAL_TYPES.map((credentialType) => (
                          <SelectItem
                            key={credentialType.value}
                            value={credentialType.value}
                            textValue={credentialType.label}
                          >
                            <div className="flex flex-col">
                              <span className="text-sm leading-5 font-medium text-foreground">
                                {credentialType.label}
                              </span>
                              <span className="text-xs leading-4 text-foreground-muted">
                                {credentialType.description}
                              </span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />
          </SidebarWrapperContent>

          <SidebarWrapperFooter>
            <Button type="submit" disabled={isSubmitDisabled} isLoading={isPending}>
              {t('common.generate')}
            </Button>
            <Button type="button" variant="ghost-neutral" onClick={handleClose}>
              {t('common.cancel')}
            </Button>
          </SidebarWrapperFooter>
        </SidebarWrapperForm>
      </Form>
    </SidebarWrapper>
  );
};
